import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Printer, Trash2, X, Settings, PackagePlus, Pencil, Loader2, TrendingUp, GripVertical } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import {
    silentPrintBarcode,
    silentPrint,
    buildNamedBarcodeLabel,
    browserPrint,
    getSavedLocalPrinter,
} from "./helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
} from "./helpers/networkPrint";
import PrinterSelectModal from "./PrinterSelectModal";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

interface Box {
    barcode: string;
    warehouse: string;
    name: string;
    length_mm: number;
    width_mm: number;
    height_mm: number;
    weight_grams: number;
    // Optional ceiling on contents weight (grams); the pack desk warns
    // past it. null = no box limit.
    max_weight_grams?: number | null;
    // Plastic mailbags only: max FILLED thickness (mm) the recommender may
    // ask the bag to take. Separate from height_mm, which is forced to 0
    // on mailbags because it leaks into volumetric weight. null = uncapped.
    max_fill_mm?: number | null;
    current_stock: number;
    supplier_name?: string | null;
    // NUMERIC arrives from pg as a string. Display layer coerces.
    unit_cost?: number | string | null;
    target_days_stock?: number | null;
    lead_time_days?: number | null;
    buffer_units?: number | null;
    min_purchase?: number | null;
    purchase_increment?: number | null;
    position?: number | null;
    wall_type?: "single" | "double" | "mailbag" | "cardboard_mailbag";
    // The warehouse's designated default "biggest" box for the pack
    // screen's recommendation — at most one per warehouse.
    is_primary_large?: boolean;
}

interface Forecast {
    barcode: string;
    name: string;
    supplier_name: string | null;
    current_stock: number;
    target_days_stock: number | null;
    lead_time_days: number | null;
    buffer_units: number | null;
    min_purchase: number | null;
    purchase_increment: number | null;
    window_days: number;
    usage: Array<{ day: string; units: number }>;
    avg_daily: number;
    total_used: number;
    to_buy: number | null;
}

// Physical label size per warehouse. Default: box labels print on the
// same stock as the Wipe module (wipe_label_width_mm/height_mm). The
// label-size modal can override that per warehouse
// (dispatch_box_label_width/height_mm) when the box printer is loaded
// with different stock.
type LabelDims = {
    w: number;               // resolved: override ?? wipe ?? 65
    h: number;               // resolved: override ?? wipe ?? 38
    wipeW: number;
    wipeH: number;
    overrideW: number | null;
    overrideH: number | null;
};

const printOneLabel = async (barcode: string, name: string, dims?: LabelDims): Promise<{ success: boolean }> => {
    // Box labels lead with the size NAME in big type — that's what the
    // operator reads when grabbing a box — with the barcode value small.
    const html = buildNamedBarcodeLabel(barcode, name, {
        emphasizeName: true,
        widthMm: dims?.w,
        heightMm: dims?.h,
    });
    const saved = getSavedNetworkPrinter();
    if (saved) {
        // The network job must carry the same name + stock size as the local
        // path — without them the remote agent renders the OLD generic
        // barcode-only ZPL at 65×38 (agents ≥ 1.5.1 honour these fields).
        const r = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, barcode, false, {
            name,
            labelWidthMm: dims?.w,
            labelHeightMm: dims?.h,
        });
        if (r.success) return { success: true };
    }
    // name → agents ≥1.4.0 render the big-name ZPL layout (≥1.5.0
    // side-by-side, sized to the configured stock via labelWidth/HeightMm);
    // fallbackHtml → non-Zebra printers get the same named layout instead
    // of the generic barcode-only label.
    const zebra = await silentPrintBarcode(barcode, 1, {
        name,
        fallbackHtml: html,
        printer: getSavedLocalPrinter() ?? undefined,
        labelWidthMm: dims?.w,
        labelHeightMm: dims?.h,
    });
    if (zebra.success) return { success: true };
    const local = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
    if (local.success) return { success: true };
    return browserPrint(html);
};

// Mailbags are measured flat (no height) — show L × W only, plus the fill
// cap when one is set. Cardboard mailbags carry a MAX filled height,
// shown as "≤ H".
const dimsCm = (b: Box) =>
    b.wall_type === "mailbag"
        ? `${(b.length_mm / 10).toFixed(0)} × ${(b.width_mm / 10).toFixed(0)} cm${b.max_fill_mm != null && b.max_fill_mm > 0 ? ` · ≤ ${(b.max_fill_mm / 10).toFixed(0)} cm` : ""}`
        : b.wall_type === "cardboard_mailbag"
        ? `${(b.length_mm / 10).toFixed(0)} × ${(b.width_mm / 10).toFixed(0)} cm · ≤ ${(b.height_mm / 10).toFixed(0)} cm`
        : `${(b.length_mm / 10).toFixed(0)} × ${(b.width_mm / 10).toFixed(0)} × ${(b.height_mm / 10).toFixed(0)} cm`;

// Inline SVG bar chart with Y-axis (units, labelled left) and X-axis
// (dates, labelled bottom). Bars are normalised to the busiest day;
// empty days render as a thin 2px stub so every day is visible. X-axis
// auto-thins date labels for longer ranges so they don't overlap.
const ForecastChart = ({
    usage,
    unitsLabel,
}: {
    usage: Array<{ day: string; units: number }>;
    unitsLabel: string;
}) => {
    const max = Math.max(1, ...usage.map(u => u.units));
    // Round the y-axis tick to a "nice" number so the gridlines look right.
    const yTickFor = (n: number) => {
        if (n <= 5) return 1;
        if (n <= 10) return 2;
        const power = Math.pow(10, Math.floor(Math.log10(n)));
        const ratio = n / power;
        if (ratio < 2) return 0.5 * power;
        if (ratio < 5) return 1 * power;
        return 2 * power;
    };
    const tick = yTickFor(max);
    const yMax = Math.ceil(max / tick) * tick;
    const yTicks: number[] = [];
    for (let v = 0; v <= yMax; v += tick) yTicks.push(v);

    const W = 600;
    const H = 220;
    const padTop = 8;
    const padBottom = 26;
    const padLeft = 38;
    const padRight = 8;
    const plotW = W - padLeft - padRight;
    const plotH = H - padTop - padBottom;
    const colW = plotW / usage.length;
    const barW = Math.max(2, Math.min(20, colW - 4));

    // Thin x-axis labels — render at most 8 distinct dates across the range.
    const labelStep = Math.max(1, Math.ceil(usage.length / 8));
    const xLabel = (iso: string, idx: number) => {
        if (idx % labelStep !== 0 && idx !== usage.length - 1) return "";
        const d = new Date(`${iso}T00:00:00`);
        // Short locale string e.g. "Apr 26" — fits comfortably in ~32px.
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    };

    return (
        <svg
            className="dispatch-forecast-chart"
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`${usage.length}-day usage`}
        >
            {/* Gridlines + Y labels */}
            {yTicks.map(v => {
                const y = padTop + plotH - (v / yMax) * plotH;
                return (
                    <g key={`y-${v}`}>
                        <line
                            x1={padLeft} x2={W - padRight} y1={y} y2={y}
                            className="dispatch-forecast-gridline"
                        />
                        <text
                            x={padLeft - 6} y={y + 3}
                            className="dispatch-forecast-axis-tick dispatch-forecast-axis-tick--y"
                        >
                            {v}
                        </text>
                    </g>
                );
            })}

            {/* Y-axis caption */}
            <text
                x={4} y={padTop + plotH / 2}
                className="dispatch-forecast-axis-caption"
                transform={`rotate(-90, 12, ${padTop + plotH / 2})`}
            >
                {unitsLabel}
            </text>

            {/* Bars */}
            {usage.map((u, i) => {
                const h = u.units > 0 ? Math.max(2, (u.units / yMax) * plotH) : 2;
                const x = padLeft + i * colW + (colW - barW) / 2;
                const y = padTop + plotH - h;
                return (
                    <rect
                        key={u.day}
                        x={x} y={y} width={barW} height={h} rx={2}
                        className={u.units > 0 ? "dispatch-forecast-bar" : "dispatch-forecast-bar dispatch-forecast-bar--empty"}
                    >
                        <title>{`${u.day}: ${u.units}`}</title>
                    </rect>
                );
            })}

            {/* X labels (dates) */}
            {usage.map((u, i) => {
                const label = xLabel(u.day, i);
                if (!label) return null;
                const x = padLeft + i * colW + colW / 2;
                return (
                    <text
                        key={`x-${u.day}`}
                        x={x} y={H - 8}
                        className="dispatch-forecast-axis-tick"
                    >
                        {label}
                    </text>
                );
            })}
        </svg>
    );
};

const BoxesAdmin = () => {
    const { t } = useTranslation();
    // Access-control read-only mode (ACCESS_CONTROL_PLAN.md §8.4). Add/edit/
    // archive/reorder/add-stock/label-size are all mutation entry points;
    // forecast, print and print-all are reads/exports and stay enabled.
    const readOnly = useReadOnly();
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [boxesByWarehouse, setBoxesByWarehouse] = useState<Record<string, Box[]>>({});
    const [loading, setLoading] = useState(true);
    // Same modal serves Add and Edit — `kind` decides which BE endpoint
    // the submit hits. Edit mode also stores the existing barcode so we
    // can target the row server-side.
    const [boxForm, setBoxForm] = useState<{
        kind: "add" | "edit";
        warehouse: string;
        barcode?: string;
        name: string;
        length: string;
        width: string;
        height: string;
        weight: string;
        maxWeight: string;
        // Plastic mailbags only — see Box.max_fill_mm.
        maxFill: string;
        wallType: "single" | "double" | "mailbag" | "cardboard_mailbag";
        isPrimaryLarge: boolean;
        supplier: string;
        unitCost: string;
        targetDays: string;
        leadTime: string;
        buffer: string;
        minPurchase: string;
        increment: string;
    } | null>(null);
    // Active suppliers — fed into the Supplier dropdown. Loaded once on
    // mount from the canonical /suppliers list (status=active) so the
    // option set matches what the Suppliers admin page exposes.
    const [activeSuppliers, setActiveSuppliers] = useState<string[]>([]);
    const [forecastBox, setForecastBox] = useState<Box | null>(null);
    const [forecast, setForecast] = useState<Forecast | null>(null);
    const [forecastLoading, setForecastLoading] = useState(false);
    const [forecastDays, setForecastDays] = useState<number>(30);

    // Drag-and-drop reorder state. dragBarcode is the barcode being held;
    // dragOverBarcode is the row currently underneath the cursor — used to
    // paint a drop-target indicator. Both clear on drop / dragEnd.
    const [dragBarcode, setDragBarcode] = useState<string | null>(null);
    const [dragOverBarcode, setDragOverBarcode] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [stockEditing, setStockEditing] = useState<{ barcode: string; units: string } | null>(null);
    // In-app confirm modal for the destructive Archive action — replaces
    // the native window.confirm so the message can be styled and translated.
    const [archiveConfirm, setArchiveConfirm] = useState<Box | null>(null);
    const [archiving, setArchiving] = useState(false);
    // Per-print loading + failure state. printingBarcode swaps the row's
    // Printer icon for a spinner; printingAllWh tracks the warehouse-level
    // loop; printerNeedsSetup highlights the Choose Printer button when a
    // job fails so the operator's eye is drawn to the configuration step.
    const [printingBarcode, setPrintingBarcode] = useState<string | null>(null);
    const [printingAllWh, setPrintingAllWh] = useState<string | null>(null);
    const [printerNeedsSetup, setPrinterNeedsSetup] = useState(false);

    // Per-warehouse label size, sourced from the Wipe label settings so
    // box labels come out on the same stock (default 65×38).
    const [labelDimsByWh, setLabelDimsByWh] = useState<Record<string, LabelDims>>({});

    // Only the FIRST load blocks the UI with the loading screen. Later
    // refreshes (after a box save / archive / stock change) update the
    // data in place — swapping to the loading screen unmounted every
    // warehouse <details>, so the section the admin was working in
    // collapsed after each save.
    const hasLoadedRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!hasLoadedRef.current) setLoading(true);
        try {
            const settingsRes = await apiFetch(`/settings`, {});
            if (!settingsRes.ok) throw new Error("settings");
            const rows: {
                warehouse: string;
                wipe_label_width_mm?: number | string | null;
                wipe_label_height_mm?: number | string | null;
                dispatch_box_label_width_mm?: number | string | null;
                dispatch_box_label_height_mm?: number | string | null;
            }[] = await settingsRes.json();
            const whList = rows.map(r => r.warehouse).sort();
            const dimsMap: Record<string, LabelDims> = {};
            for (const r of rows) {
                const wipeW = Number(r.wipe_label_width_mm) || 65;
                const wipeH = Number(r.wipe_label_height_mm) || 38;
                const overrideW = Number(r.dispatch_box_label_width_mm) || null;
                const overrideH = Number(r.dispatch_box_label_height_mm) || null;
                dimsMap[r.warehouse] = {
                    w: overrideW ?? wipeW,
                    h: overrideH ?? wipeH,
                    wipeW,
                    wipeH,
                    overrideW,
                    overrideH,
                };
            }
            setLabelDimsByWh(dimsMap);
            const map: Record<string, Box[]> = {};
            await Promise.all(whList.map(async wh => {
                const r = await apiFetch(
                    `/dispatch-admin/boxes?warehouse=${encodeURIComponent(wh)}`,
                    {},
                );
                if (r.ok) {
                    const data = await r.json();
                    map[wh] = Array.isArray(data.boxes) ? data.boxes : [];
                } else {
                    map[wh] = [];
                }
            }));
            setWarehouses(whList);
            setBoxesByWarehouse(map);
        } catch {
            toast.error(t("server_error"), { toastId: "boxes-load-err" });
        } finally {
            hasLoadedRef.current = true;
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { refresh(); }, [refresh]);

    // Pull the active supplier list once. Page size 500 covers any
    // realistic supplier count; if it ever overflows the BE caps at 500
    // and the dropdown will simply be missing the long-tail entries.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(
                    `/suppliers?status=active&page=0&page_size=500`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                const names = Array.isArray(data?.suppliers)
                    ? (data.suppliers as Array<{ name: string }>).map(s => s.name).sort()
                    : [];
                setActiveSuppliers(names);
            } catch { /* leave empty — dropdown shows the empty option only */ }
        })();
    }, []);

    const handleSubmitForm = async () => {
        if (!boxForm) return;
        // Plastic mailbags are measured flat — no height; stored as 0 and
        // ignored by the recommender's wrap-around maths. Cardboard
        // mailbags require a height: it's the MAX filled height.
        const isMailbag = boxForm.wallType === "mailbag";
        const len = Number(boxForm.length);
        const wid = Number(boxForm.width);
        const hgt = isMailbag ? 0 : Number(boxForm.height);
        const wgt = Number(boxForm.weight);
        const maxWgtRaw = boxForm.maxWeight.trim();
        const maxWgt = maxWgtRaw === "" ? null : Number(maxWgtRaw);
        if (maxWgt !== null && (!Number.isFinite(maxWgt) || maxWgt <= 0)) {
            toast.error(t("box_max_weight_invalid"), { toastId: "box-form-err" });
            return;
        }
        // The fill cap only means anything on a plastic mailbag; any other
        // wall type stores null so a bag flipped to a box drops its cap.
        const maxFillRaw = isMailbag ? boxForm.maxFill.trim() : "";
        const maxFill = maxFillRaw === "" ? null : Number(maxFillRaw);
        if (maxFill !== null && (!Number.isFinite(maxFill) || maxFill <= 0)) {
            toast.error(t("box_max_fill_invalid"), { toastId: "box-form-err" });
            return;
        }
        if (!boxForm.name.trim() || !Number.isFinite(len) || len <= 0
            || !Number.isFinite(wid) || wid <= 0
            || (!isMailbag && (!Number.isFinite(hgt) || hgt <= 0))
            || !Number.isFinite(wgt) || wgt < 0) {
            toast.error(t("box_form_invalid"), { toastId: "box-form-err" });
            return;
        }
        setSubmitting(true);
        try {
            const isEdit = boxForm.kind === "edit";
            const path = isEdit ? "/dispatch-admin/boxes/update" : "/dispatch-admin/boxes/add";
            const body: Record<string, unknown> = {
                name: boxForm.name.trim(),
                length_mm: Math.round(len),
                width_mm: Math.round(wid),
                height_mm: Math.round(hgt),
                weight_grams: Math.round(wgt),
                max_weight_grams: maxWgt === null ? null : Math.round(maxWgt),
                max_fill_mm: maxFill === null ? null : Math.round(maxFill),
                // Optional reorder fields. Empty strings store as null
                // (BE sanitisers handle the conversion).
                supplier_name: boxForm.supplier.trim() || null,
                unit_cost: boxForm.unitCost.trim() === "" ? null : Number(boxForm.unitCost),
                target_days_stock: boxForm.targetDays === "" ? null : Number(boxForm.targetDays),
                lead_time_days: boxForm.leadTime === "" ? null : Number(boxForm.leadTime),
                buffer_units: boxForm.buffer === "" ? null : Number(boxForm.buffer),
                min_purchase: boxForm.minPurchase === "" ? null : Number(boxForm.minPurchase),
                purchase_increment: boxForm.increment === "" ? null : Number(boxForm.increment),
                wall_type: boxForm.wallType,
                is_primary_large: boxForm.isPrimaryLarge,
            };
            if (isEdit) body.barcode = boxForm.barcode;
            else body.warehouse = boxForm.warehouse;

            const res = await apiFetch(`${path}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error(isEdit ? "update" : "add");
            // Close the modal the moment the save lands; the list refresh
            // happens in place behind it.
            setBoxForm(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "box-form-err-server" });
        } finally {
            setSubmitting(false);
        }
    };

    const confirmArchive = async () => {
        if (!archiveConfirm) return;
        setArchiving(true);
        try {
            const res = await apiFetch(`/dispatch-admin/boxes/archive`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: archiveConfirm.barcode }),
            });
            if (!res.ok) throw new Error("archive");
            await refresh();
            setArchiveConfirm(null);
        } catch {
            toast.error(t("server_error"), { toastId: "box-archive-err" });
        } finally {
            setArchiving(false);
        }
    };

    const handleAddStock = async () => {
        if (!stockEditing) return;
        const n = Number(stockEditing.units);
        if (!Number.isFinite(n) || n < 1) {
            toast.error(t("box_units_invalid"), { toastId: "box-stock-err" });
            return;
        }
        try {
            const res = await apiFetch(`/dispatch-admin/boxes/add-stock`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: stockEditing.barcode, units: Math.round(n) }),
            });
            if (!res.ok) throw new Error("stock");
            await refresh();
            setStockEditing(null);
        } catch {
            toast.error(t("server_error"), { toastId: "box-stock-server-err" });
        }
    };

    // Loads (or reloads) the forecast for the targeted box at the chosen
    // day range. Exposed as a separate fn so the day-range pills inside
    // the modal can re-trigger it without bouncing through openForecast.
    const loadForecast = useCallback(async (barcode: string, days: number) => {
        setForecastLoading(true);
        try {
            const res = await apiFetch(
                `/dispatch-admin/box/forecast?barcode=${encodeURIComponent(barcode)}&days=${days}`,
                {},
            );
            if (!res.ok) throw new Error("forecast");
            const data: Forecast = await res.json();
            setForecast(data);
        } catch {
            toast.error(t("server_error"), { toastId: "box-forecast-err" });
        } finally {
            setForecastLoading(false);
        }
    }, [t]);

    const openForecast = (box: Box) => {
        setForecastBox(box);
        setForecast(null);
        setForecastDays(30);
        loadForecast(box.barcode, 30);
    };

    // Drag-and-drop reorder handler. Computes the new order and POSTs it
    // optimistically — local state updates immediately so the row "snaps"
    // into the dropped position even if the BE round trip is slow.
    const handleReorder = async (warehouse: string, draggedBarcode: string, dropOnBarcode: string) => {
        if (draggedBarcode === dropOnBarcode) return;
        const list = boxesByWarehouse[warehouse] ?? [];
        const fromIdx = list.findIndex(b => b.barcode === draggedBarcode);
        const toIdx = list.findIndex(b => b.barcode === dropOnBarcode);
        if (fromIdx < 0 || toIdx < 0) return;
        const next = [...list];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setBoxesByWarehouse(prev => ({ ...prev, [warehouse]: next }));
        try {
            await apiFetch(`/dispatch-admin/boxes/reorder`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, barcodes: next.map(b => b.barcode) }),
            });
        } catch {
            toast.error(t("server_error"), { toastId: "box-reorder-err" });
            await refresh();
        }
    };

    const handlePrint = async (box: Box) => {
        setPrintingBarcode(box.barcode);
        try {
            const r = await printOneLabel(box.barcode, box.name, labelDimsByWh[box.warehouse]);
            if (!r.success) {
                setPrinterNeedsSetup(true);
                toast.error(t("print_failed"), { toastId: "box-print-err" });
            }
        } finally {
            setPrintingBarcode(null);
        }
    };

    // Label-size modal — per-warehouse override for the box label stock.
    // Blank fields follow the warehouse's Wipe label size.
    const [labelSizeCfg, setLabelSizeCfg] = useState<{ warehouse: string; width: string; height: string } | null>(null);
    const [labelSizeSaving, setLabelSizeSaving] = useState(false);

    const saveLabelSize = async () => {
        if (!labelSizeCfg || labelSizeSaving) return;
        setLabelSizeSaving(true);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(labelSizeCfg.warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dispatch_box_label_width_mm: labelSizeCfg.width.trim() === "" ? null : Number(labelSizeCfg.width),
                    dispatch_box_label_height_mm: labelSizeCfg.height.trim() === "" ? null : Number(labelSizeCfg.height),
                }),
            });
            if (!res.ok) throw new Error("label-size");
            setLabelSizeCfg(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "box-label-size-err" });
        } finally {
            setLabelSizeSaving(false);
        }
    };

    const handlePrintAll = async (warehouse: string) => {
        const list = boxesByWarehouse[warehouse] ?? [];
        if (list.length === 0) return;
        setPrintingAllWh(warehouse);
        try {
            let fail = 0;
            for (const b of list) {
                const r = await printOneLabel(b.barcode, b.name, labelDimsByWh[warehouse]);
                if (!r.success) fail += 1;
            }
            if (fail > 0) {
                setPrinterNeedsSetup(true);
                toast.warn(t("print_partial_failed", { failed: String(fail), total: String(list.length) }));
            }
        } finally {
            setPrintingAllWh(null);
        }
    };

    if (loading) return <div className="admin-settings-loading">{t("loading")}</div>;

    return (
        <div>
            <div className="dispatch-admin-toolbar">
                <button
                    type="button"
                    className={`btn btn-ghost btn-sm ${printerNeedsSetup ? "dispatch-admin-printer-needed" : ""}`}
                    onClick={() => { setShowPrinterSelect(true); setPrinterNeedsSetup(false); }}
                >
                    <Settings size={14} />
                    {t("printer_select")}
                </button>
            </div>

            {warehouses.length === 0 && <div className="admin-settings-empty">{t("no_warehouses")}</div>}

            {warehouses.map(wh => {
                const list = boxesByWarehouse[wh] ?? [];
                return (
                    <details key={wh} className="admin-settings-po-warehouse">
                        <summary className="admin-settings-po-warehouse-summary">
                            <span className="admin-settings-po-warehouse-name">{wh}</span>
                            <span className="admin-settings-po-warehouse-count">{list.length}</span>
                        </summary>
                        <div className="admin-settings-po-editor">
                            <div className="dispatch-admin-rows">
                                {list.length > 0 && (
                                    <div className="dispatch-admin-row dispatch-admin-row--box dispatch-admin-row--head">
                                        <span aria-hidden="true" />
                                        <span>{t("box_name")}</span>
                                        <span>{t("barcode_label")}</span>
                                        <span>{t("dimensions_label")}</span>
                                        <span style={{ textAlign: "right" }}>{t("box_stock")}</span>
                                        <span aria-hidden="true" />
                                    </div>
                                )}
                                {list.map(b => (
                                    <div
                                        key={b.barcode}
                                        className={`dispatch-admin-row dispatch-admin-row--box ${dragBarcode === b.barcode ? "dispatch-admin-row--dragging" : ""} ${dragOverBarcode === b.barcode && dragBarcode && dragBarcode !== b.barcode ? "dispatch-admin-row--drop-target" : ""}`}
                                        draggable={!readOnly}
                                        onDragStart={(e) => {
                                            if (readOnly) return;
                                            setDragBarcode(b.barcode);
                                            e.dataTransfer.effectAllowed = "move";
                                            // Use the barcode as the payload so the
                                            // browser surfaces a sensible drag image
                                            // text on environments that show one.
                                            e.dataTransfer.setData("text/plain", b.barcode);
                                        }}
                                        onDragOver={(e) => {
                                            if (!dragBarcode || readOnly) return;
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                            if (dragOverBarcode !== b.barcode) setDragOverBarcode(b.barcode);
                                        }}
                                        onDragLeave={() => {
                                            if (dragOverBarcode === b.barcode) setDragOverBarcode(null);
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (dragBarcode && !readOnly) handleReorder(wh, dragBarcode, b.barcode);
                                            setDragBarcode(null);
                                            setDragOverBarcode(null);
                                        }}
                                        onDragEnd={() => {
                                            setDragBarcode(null);
                                            setDragOverBarcode(null);
                                        }}
                                    >
                                        <span className="dispatch-admin-row-handle" title={t("drag_to_reorder")}>
                                            <GripVertical size={14} />
                                        </span>
                                        {/* Badge lives INSIDE the name cell — the row is a
                                            fixed 6-column grid, so an extra sibling would
                                            shift every column and wrap the row. */}
                                        <span className="dispatch-admin-row-name">
                                            {b.name}
                                            {b.is_primary_large && (
                                                <span className="dispatch-admin-row-primary" title={t("box_primary_large_hint")}>
                                                    {t("box_primary_large_badge")}
                                                </span>
                                            )}
                                        </span>
                                        <span className="dispatch-admin-row-barcode">{b.barcode}</span>
                                        <span className="dispatch-admin-row-meta">
                                            {dimsCm(b)} &middot; {b.weight_grams}g
                                            {b.max_weight_grams != null && (
                                                <> &middot; {t("box_max_weight_meta", { kg: (b.max_weight_grams / 1000).toFixed(1) })}</>
                                            )}
                                        </span>
                                        <span className="dispatch-admin-row-stock">
                                            {Math.max(0, b.current_stock)}
                                        </span>
                                        <div className="dispatch-admin-row-actions-inline">
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => setStockEditing({ barcode: b.barcode, units: "" })}
                                                title={readOnly ? t("read_only_mode") : t("add_inventory")}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                            >
                                                <PackagePlus size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => openForecast(b)}
                                                title={t("forecast")}
                                            >
                                                <TrendingUp size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => setBoxForm({
                                                    kind: "edit",
                                                    warehouse: b.warehouse,
                                                    barcode: b.barcode,
                                                    name: b.name,
                                                    length: String(b.length_mm),
                                                    width: String(b.width_mm),
                                                    height: String(b.height_mm),
                                                    weight: String(b.weight_grams),
                                                    maxWeight: b.max_weight_grams != null ? String(b.max_weight_grams) : "",
                                                    maxFill: b.max_fill_mm != null ? String(b.max_fill_mm) : "",
                                                    wallType: b.wall_type === "double" || b.wall_type === "mailbag" || b.wall_type === "cardboard_mailbag" ? b.wall_type : "single",
                                                    isPrimaryLarge: b.is_primary_large === true,
                                                    supplier: b.supplier_name ?? "",
                                                    unitCost: b.unit_cost != null ? String(b.unit_cost) : "",
                                                    targetDays: b.target_days_stock != null ? String(b.target_days_stock) : "",
                                                    leadTime: b.lead_time_days != null ? String(b.lead_time_days) : "",
                                                    buffer: b.buffer_units != null ? String(b.buffer_units) : "",
                                                    minPurchase: b.min_purchase != null ? String(b.min_purchase) : "",
                                                    increment: b.purchase_increment != null ? String(b.purchase_increment) : "",
                                                })}
                                                title={readOnly ? t("read_only_mode") : t("edit_box")}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => handlePrint(b)}
                                                title={t("print_label")}
                                                disabled={printingBarcode === b.barcode || printingAllWh === wh}
                                            >
                                                {printingBarcode === b.barcode || printingAllWh === wh
                                                    ? <Loader2 size={16} className="dispatch-admin-spin" />
                                                    : <Printer size={16} />}
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                                onClick={() => setArchiveConfirm(b)}
                                                title={readOnly ? t("read_only_mode") : t("archive_box")}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {list.length === 0 && (
                                    <div className="admin-settings-empty">{t("no_boxes_yet")}</div>
                                )}
                            </div>
                            <div className="dispatch-admin-row-actions">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setBoxForm({ kind: "add", warehouse: wh, name: "", length: "", width: "", height: "", weight: "", maxWeight: "", maxFill: "", wallType: "single", isPrimaryLarge: false, supplier: "", unitCost: "", targetDays: "", leadTime: "", buffer: "", minPurchase: "", increment: "" })}
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Plus size={14} />
                                    {t("add_box")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handlePrintAll(wh)}
                                    disabled={list.length === 0 || printingAllWh === wh}
                                >
                                    {printingAllWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Printer size={14} />}
                                    {t("print_all")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setLabelSizeCfg({
                                        warehouse: wh,
                                        width: labelDimsByWh[wh]?.overrideW != null ? String(labelDimsByWh[wh].overrideW) : "",
                                        height: labelDimsByWh[wh]?.overrideH != null ? String(labelDimsByWh[wh].overrideH) : "",
                                    })}
                                    title={readOnly ? t("read_only_mode") : t("box_label_size_title")}
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                >
                                    <Settings size={14} />
                                    {t("box_label_size_btn")}
                                </button>
                            </div>
                        </div>
                    </details>
                );
            })}

            {boxForm && (
                <div className="modal-overlay" onClick={() => !submitting && setBoxForm(null)}>
                    <div className="modal-card dispatch-admin-form-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>
                                {boxForm.kind === "edit" ? t("edit_box") : t("add_box")}
                                {boxForm.kind === "add" && ` — ${boxForm.warehouse}`}
                                {boxForm.kind === "edit" && boxForm.barcode && ` — ${boxForm.barcode}`}
                            </h2>
                            <button className="modal-close" onClick={() => setBoxForm(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("box_name")}
                                <input
                                    type="text"
                                    autoFocus
                                    className="dispatch-admin-form-input"
                                    value={boxForm.name}
                                    onChange={e => setBoxForm({ ...boxForm, name: e.target.value })}
                                    placeholder="Small"
                                />
                            </label>
                            <p className="admin-settings-section-hint" style={{ marginBottom: 8 }}>
                                {t("box_dimensions_external_hint")}
                            </p>
                            <div className="dispatch-admin-form-grid dispatch-admin-form-grid--dims">
                                <label className="dispatch-admin-form-label">
                                    {t("box_length_mm")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-admin-form-input"
                                        value={boxForm.length}
                                        onChange={e => setBoxForm({ ...boxForm, length: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_width_mm")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-admin-form-input"
                                        value={boxForm.width}
                                        onChange={e => setBoxForm({ ...boxForm, width: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {boxForm.wallType === "cardboard_mailbag" ? t("box_max_height_mm") : t("box_height_mm")}
                                    {/* Plastic mailbags are measured flat — no height to
                                        enter. Cardboard mailbags take a MAX filled height. */}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-admin-form-input"
                                        value={boxForm.wallType === "mailbag" ? "" : boxForm.height}
                                        placeholder={boxForm.wallType === "mailbag" ? t("box_height_na_mailbag") : undefined}
                                        disabled={boxForm.wallType === "mailbag"}
                                        onChange={e => setBoxForm({ ...boxForm, height: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_weight_g")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-admin-form-input"
                                        value={boxForm.weight}
                                        onChange={e => setBoxForm({ ...boxForm, weight: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_max_weight_g")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-admin-form-input"
                                        value={boxForm.maxWeight}
                                        placeholder={t("box_max_weight_placeholder")}
                                        onChange={e => setBoxForm({ ...boxForm, maxWeight: e.target.value })}
                                    />
                                </label>
                                {/* Plastic mailbags only: how thick the filled bag may get
                                    before the recommender stops offering it. Kept apart from
                                    height, which is forced to 0 on a mailbag so it never
                                    reaches volumetric weight or a carrier. */}
                                {boxForm.wallType === "mailbag" && (
                                    <label className="dispatch-admin-form-label" title={t("box_max_fill_hint")}>
                                        {t("box_max_fill_mm")}
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            className="dispatch-admin-form-input"
                                            value={boxForm.maxFill}
                                            placeholder={t("box_max_fill_placeholder")}
                                            onChange={e => setBoxForm({ ...boxForm, maxFill: e.target.value })}
                                        />
                                    </label>
                                )}
                            </div>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("box_wall_type")}
                                <select
                                    className="dispatch-admin-form-input"
                                    value={boxForm.wallType}
                                    onChange={e => setBoxForm({ ...boxForm, wallType: e.target.value as "single" | "double" | "mailbag" | "cardboard_mailbag" })}
                                >
                                    <option value="single">{t("box_wall_type_single")}</option>
                                    <option value="double">{t("box_wall_type_double")}</option>
                                    <option value="mailbag">{t("box_wall_type_mailbag")}</option>
                                    <option value="cardboard_mailbag">{t("box_wall_type_cardboard_mailbag")}</option>
                                </select>
                            </label>
                            <p className="admin-settings-section-hint" style={{ marginTop: 4 }}>
                                {t("box_wall_type_hint")}
                            </p>

                            <label className="dispatch-admin-form-label" style={{ marginTop: 12, flexDirection: "row", alignItems: "center", gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={boxForm.isPrimaryLarge}
                                    onChange={e => setBoxForm({ ...boxForm, isPrimaryLarge: e.target.checked })}
                                />
                                {t("box_primary_large")}
                            </label>
                            <p className="admin-settings-section-hint" style={{ marginTop: 4 }}>
                                {t("box_primary_large_hint")}
                            </p>

                            {/* Supplier + reorder fields. All optional —
                                drives the per-box forecast modal. */}
                            <h3 className="dispatch-admin-form-section">{t("box_reorder_section")}</h3>
                            <div className="dispatch-admin-form-grid">
                                <label className="dispatch-admin-form-label">
                                    {t("box_supplier_name")}
                                    {/* Dropdown sourced from the canonical
                                        suppliers table. If the box has a
                                        supplier that's since been archived
                                        (so it isn't in activeSuppliers), we
                                        still surface it as a disabled option
                                        so the operator can see the stale
                                        value and pick a replacement. */}
                                    <select
                                        className="dispatch-admin-form-input"
                                        value={boxForm.supplier}
                                        onChange={e => setBoxForm({ ...boxForm, supplier: e.target.value })}
                                    >
                                        <option value="">{t("box_supplier_none")}</option>
                                        {boxForm.supplier && !activeSuppliers.includes(boxForm.supplier) && (
                                            <option value={boxForm.supplier} disabled>
                                                {boxForm.supplier} ({t("box_supplier_inactive")})
                                            </option>
                                        )}
                                        {activeSuppliers.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_unit_cost")}
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="0.01"
                                        className="dispatch-admin-form-input"
                                        value={boxForm.unitCost}
                                        onChange={e => setBoxForm({ ...boxForm, unitCost: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </label>
                            </div>
                            <div className="dispatch-admin-form-grid">
                                <label className="dispatch-admin-form-label">
                                    {t("box_target_days")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        className="dispatch-admin-form-input"
                                        value={boxForm.targetDays}
                                        onChange={e => setBoxForm({ ...boxForm, targetDays: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_lead_time")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        className="dispatch-admin-form-input"
                                        value={boxForm.leadTime}
                                        onChange={e => setBoxForm({ ...boxForm, leadTime: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_buffer_units")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        className="dispatch-admin-form-input"
                                        value={boxForm.buffer}
                                        onChange={e => setBoxForm({ ...boxForm, buffer: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_min_purchase")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        className="dispatch-admin-form-input"
                                        value={boxForm.minPurchase}
                                        onChange={e => setBoxForm({ ...boxForm, minPurchase: e.target.value })}
                                        placeholder="1000"
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_purchase_increment")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={1}
                                        className="dispatch-admin-form-input"
                                        value={boxForm.increment}
                                        onChange={e => setBoxForm({ ...boxForm, increment: e.target.value })}
                                        placeholder="100"
                                    />
                                </label>
                            </div>

                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setBoxForm(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={handleSubmitForm}
                                    disabled={submitting || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : (boxForm.kind === "edit" ? t("save") : t("add_box"))}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {stockEditing && (
                <div className="modal-overlay" onClick={() => setStockEditing(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 320 }}>
                        <div className="modal-card-header">
                            <h2>{t("add_inventory")}</h2>
                            <button className="modal-close" onClick={() => setStockEditing(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p style={{ marginBottom: 12, color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                                {stockEditing.barcode}
                            </p>
                            <label className="dispatch-admin-form-label">
                                {t("add_units")}
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    autoFocus
                                    min={1}
                                    className="dispatch-admin-form-input"
                                    value={stockEditing.units}
                                    onChange={e => setStockEditing({ ...stockEditing, units: e.target.value })}
                                    onKeyDown={e => { if (e.key === "Enter") handleAddStock(); }}
                                />
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setStockEditing(null)}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleAddStock}
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {t("add")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Label-size modal — box labels follow the warehouse's Wipe
                label size by default; a value here overrides that for box
                labels only. Blank = follow Wipe. */}
            {labelSizeCfg && (
                <div className="modal-overlay" onClick={() => setLabelSizeCfg(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("box_label_size_title")} — {labelSizeCfg.warehouse}</h2>
                            <button className="modal-close" onClick={() => setLabelSizeCfg(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-card-body">
                            <p className="admin-settings-section-hint" style={{ marginBottom: 12 }}>
                                {t("box_label_size_hint", {
                                    w: String(labelDimsByWh[labelSizeCfg.warehouse]?.wipeW ?? 65),
                                    h: String(labelDimsByWh[labelSizeCfg.warehouse]?.wipeH ?? 38),
                                })}
                            </p>
                            <div className="dispatch-admin-form-grid">
                                <label className="dispatch-admin-form-label">
                                    {t("box_label_width_mm")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={20}
                                        max={300}
                                        className="dispatch-admin-form-input"
                                        placeholder={String(labelDimsByWh[labelSizeCfg.warehouse]?.wipeW ?? 65)}
                                        value={labelSizeCfg.width}
                                        onChange={e => setLabelSizeCfg({ ...labelSizeCfg, width: e.target.value })}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("box_label_height_mm")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={20}
                                        max={300}
                                        className="dispatch-admin-form-input"
                                        placeholder={String(labelDimsByWh[labelSizeCfg.warehouse]?.wipeH ?? 38)}
                                        value={labelSizeCfg.height}
                                        onChange={e => setLabelSizeCfg({ ...labelSizeCfg, height: e.target.value })}
                                    />
                                </label>
                            </div>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setLabelSizeCfg(null)}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={saveLabelSize}
                                    disabled={labelSizeSaving || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {labelSizeSaving ? <Loader2 size={14} className="dispatch-admin-spin" /> : null}
                                    {t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {forecastBox && (
                <div className="modal-overlay" onClick={() => setForecastBox(null)}>
                    <div className="modal-card dispatch-forecast-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>
                                {t("forecast")} — {forecastBox.name}
                                <span className="dispatch-forecast-barcode">{forecastBox.barcode}</span>
                            </h2>
                            <button className="modal-close" onClick={() => setForecastBox(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            {/* Day-range pill selector — re-fetches the
                                forecast on click. Sits at the top so the
                                operator picks scope first, then reads the
                                chart + recommendation. */}
                            <div className="dispatch-forecast-range">
                                {[7, 30, 60, 90].map(d => (
                                    <button
                                        key={d}
                                        type="button"
                                        className={`dispatch-forecast-range-btn ${forecastDays === d ? "dispatch-forecast-range-btn--active" : ""}`}
                                        onClick={() => {
                                            if (forecastDays === d) return;
                                            setForecastDays(d);
                                            if (forecastBox) loadForecast(forecastBox.barcode, d);
                                        }}
                                    >
                                        {t("forecast_range_days", { days: String(d) })}
                                    </button>
                                ))}
                            </div>

                            {forecastLoading || !forecast ? (
                                <div className="admin-settings-loading">{t("loading")}</div>
                            ) : (
                                <>
                                    {/* Top stat: avg per day, big number */}
                                    <div className="dispatch-forecast-headline">
                                        <div className="dispatch-forecast-headline-num">{forecast.avg_daily.toFixed(1)}</div>
                                        <div className="dispatch-forecast-headline-label">
                                            {t("forecast_avg_daily_range", { days: String(forecast.window_days) })}
                                        </div>
                                    </div>

                                    {/* SVG bar chart with axes — Y label "Units"
                                        on the left, dates along the bottom. The
                                        ForecastChart auto-thins X labels for
                                        ranges > 14 days so the axis stays legible. */}
                                    <ForecastChart usage={forecast.usage} unitsLabel={t("forecast_units")} />

                                    {/* Buy recommendation. Suppressed when
                                        the box has no reorder config. */}
                                    {forecast.to_buy != null ? (
                                        <div className="dispatch-forecast-recommend">
                                            <div className="dispatch-forecast-recommend-label">
                                                {t("forecast_to_buy")}
                                            </div>
                                            <div className="dispatch-forecast-recommend-num">
                                                {forecast.to_buy}
                                                <span className="dispatch-forecast-recommend-units"> {t("forecast_units")}</span>
                                            </div>
                                            {forecast.supplier_name && (
                                                <div className="dispatch-forecast-recommend-supplier">
                                                    {t("forecast_from")} <strong>{forecast.supplier_name}</strong>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="dispatch-forecast-no-config">
                                            {t("forecast_no_config")}
                                        </div>
                                    )}

                                    {/* Reorder config summary so the user
                                        can see what the recommendation is
                                        based on (and edit via the pencil
                                        icon if anything's missing). */}
                                    <div className="dispatch-forecast-config">
                                        <div><span>{t("forecast_current_stock")}</span><strong>{forecast.current_stock}</strong></div>
                                        <div><span>{t("box_target_days")}</span><strong>{forecast.target_days_stock ?? "—"}</strong></div>
                                        <div><span>{t("box_lead_time")}</span><strong>{forecast.lead_time_days ?? "—"}</strong></div>
                                        <div><span>{t("box_buffer_units")}</span><strong>{forecast.buffer_units ?? "—"}</strong></div>
                                        <div><span>{t("box_min_purchase")}</span><strong>{forecast.min_purchase ?? "—"}</strong></div>
                                        <div><span>{t("box_purchase_increment")}</span><strong>{forecast.purchase_increment ?? "—"}</strong></div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {archiveConfirm && (
                <div className="modal-overlay" onClick={() => !archiving && setArchiveConfirm(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("archive_box")}</h2>
                            <button className="modal-close" onClick={() => setArchiveConfirm(null)} disabled={archiving}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
                                {t("confirm_archive_box", { name: archiveConfirm.name })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => setArchiveConfirm(null)}
                                    disabled={archiving}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={confirmArchive}
                                    disabled={archiving || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {archiving ? t("submitting") : t("archive_box")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={() => setShowPrinterSelect(false)}
                    onConnectLocal={() => setShowPrinterSelect(false)}
                    onDisconnect={() => setShowPrinterSelect(false)}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default BoxesAdmin;
